import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, Globe, Search, TrendingUp, Star, ExternalLink } from 'lucide-react';

const Home: React.FC = () => {
  const services = [
    {
      icon: <Code className="h-8 w-8" />,
      title: 'App Development',
      description: 'Native and cross-platform mobile applications built with cutting-edge technologies.',
      features: ['iOS & Android', 'React Native', 'Flutter', 'Progressive Web Apps']
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: 'Web Development',
      description: 'Modern, responsive websites and web applications that drive business growth.',
      features: ['React & Next.js', 'Node.js & Express', 'E-commerce Solutions', 'Custom CMS']
    },
    {
      icon: <Search className="h-8 w-8" />,
      title: 'SEO Services',
      description: 'Comprehensive SEO strategies to improve your search engine rankings and visibility.',
      features: ['Technical SEO', 'Content Optimization', 'Local SEO', 'Analytics & Reporting']
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: 'Marketing Consultancy',
      description: 'Strategic digital marketing solutions to accelerate your business growth.',
      features: ['Digital Strategy', 'Social Media Marketing', 'PPC Campaigns', 'Brand Development']
    }
  ];

  const caseStudies = [
    {
      id: '1',
      title: 'E-commerce Platform Redesign',
      description: 'Complete redesign and development of a multi-vendor e-commerce platform resulting in 150% increase in sales.',
      image: '/images/case-study-1.jpg',
      technologies: ['React', 'Node.js', 'PostgreSQL', 'Stripe'],
      results: '150% increase in sales'
    },
    {
      id: '2',
      title: 'Healthcare Mobile App',
      description: 'Cross-platform mobile application for healthcare providers with real-time patient monitoring.',
      image: '/images/case-study-2.jpg',
      technologies: ['React Native', 'Firebase', 'WebRTC'],
      results: '10,000+ active users'
    },
    {
      id: '3',
      title: 'SaaS Dashboard Development',
      description: 'Modern analytics dashboard for a SaaS platform with real-time data visualization.',
      image: '/images/case-study-3.jpg',
      technologies: ['Next.js', 'D3.js', 'MongoDB'],
      results: '40% improved user engagement'
    }
  ];

  const testimonials = [
    {
      id: '1',
      name: 'Sarah Johnson',
      company: 'TechStart Inc.',
      content: 'Infurasoft delivered an exceptional mobile app that exceeded our expectations. Their attention to detail and technical expertise is outstanding.',
      rating: 5,
      avatar: '/images/testimonial-1.jpg'
    },
    {
      id: '2',
      name: 'Michael Chen',
      company: 'Digital Solutions Ltd.',
      content: 'The team at Infurasoft transformed our outdated website into a modern, high-performing platform. Highly recommended!',
      rating: 5,
      avatar: '/images/testimonial-2.jpg'
    },
    {
      id: '3',
      name: 'Emily Rodriguez',
      company: 'Growth Marketing Co.',
      content: 'Their SEO services helped us achieve first-page rankings for our target keywords. The ROI has been incredible.',
      rating: 5,
      avatar: '/images/testimonial-3.jpg'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="mb-4">
                <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30">
                  🚀 Digital Innovation Partner
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight">
                Transform Your
                <span className="block text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                  Digital Future
                </span>
              </h1>
              <p className="text-lg lg:text-xl mb-6 text-blue-100 leading-relaxed max-w-2xl">
                We are Infurasoft, a leading digital agency specializing in app development, web development, SEO, and marketing consultancy. Let's build something amazing together.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold rounded-lg bg-white text-blue-600 hover:bg-blue-50 transition-all duration-200 hover:scale-105 shadow-lg"
                >
                  Start Your Project
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold rounded-lg border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-all duration-200 hover:scale-105"
                >
                  Explore Services
                </Link>
              </div>
              
              {/* Trust Indicators */}
              <div className="flex items-center justify-center lg:justify-start gap-6 text-sm text-blue-200">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  <span>Trusted by 200+ Companies</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 border border-white/20 shadow-2xl">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/20 rounded-xl p-3 text-center hover:bg-white/30 transition-colors duration-300">
                    <div className="text-2xl font-bold">200+</div>
                    <div className="text-blue-200 text-xs">Projects</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 text-center hover:bg-white/30 transition-colors duration-300">
                    <div className="text-2xl font-bold">98%</div>
                    <div className="text-blue-200 text-xs">Satisfaction</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 text-center hover:bg-white/30 transition-colors duration-300">
                    <div className="text-2xl font-bold">5+</div>
                    <div className="text-blue-200 text-xs">Years</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-3 text-center hover:bg-white/30 transition-colors duration-300">
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-blue-200 text-xs">Support</div>
                  </div>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-2 -right-2 w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm animate-pulse">
                  ⭐
                </div>
                <div className="absolute -bottom-2 -left-2 w-12 h-12 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs animate-bounce">
                  🚀
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-blue-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium mb-6">
              🚀 Our Expertise
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Comprehensive <span className="text-blue-600 dark:text-blue-400">Digital Solutions</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              We offer end-to-end digital transformation services that help businesses thrive in the modern digital landscape with cutting-edge technology and strategic expertise.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="group relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-blue-200 dark:hover:border-blue-600 hover:-translate-y-2 hover:scale-105">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent dark:from-blue-900/20 dark:to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-2xl mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="text-sm text-gray-500 dark:text-gray-400 flex items-center group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                        <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mr-3 flex-shrink-0 group-hover:scale-125 transition-transform duration-300"></div>
                        <span className="line-clamp-1">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500/20 to-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-16">
            <Link
              to="/services"
              className="group inline-flex items-center px-10 py-4 text-lg font-semibold rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              Explore All Services
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-blue-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 rounded-full text-sm font-medium mb-6">
              📊 Success Stories
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Proven <span className="text-green-600 dark:text-green-400">Results</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Discover how we've helped businesses achieve remarkable digital transformation goals through innovative solutions and strategic implementation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {caseStudies.map((study) => (
              <div key={study.id} className="group relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-green-200 dark:hover:border-green-600 hover:-translate-y-3 hover:scale-105">
                {/* Header with Achievement */}
                <div className="relative h-48 bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-500"></div>
                  
                  {/* Animated Background Pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm-20 0c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20z'/%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                  </div>
                  
                  <div className="text-white text-center relative z-10">
                    <div className="text-2xl font-bold mb-2 group-hover:scale-110 transition-transform duration-500">{study.results}</div>
                    <div className="text-green-100 text-sm font-medium">Achievement</div>
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white text-sm animate-pulse">
                    ⭐
                  </div>
                  <div className="absolute bottom-4 left-4 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-bounce">
                    🚀
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-300">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm leading-relaxed line-clamp-3">
                    {study.description}
                  </p>
                  
                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {study.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/50 dark:to-emerald-900/50 text-green-800 dark:text-green-200 text-xs rounded-full font-medium border border-green-200/50 dark:border-green-700/50 group-hover:scale-105 transition-transform duration-300">
                        {tech}
                      </span>
                    ))}
                  </div>
                  
                  {/* CTA Button */}
                  <button className="group/btn w-full inline-flex items-center justify-center px-6 py-3 text-sm font-semibold rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                    <span>View Case Study</span>
                    <ExternalLink className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-green-500/20 to-emerald-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-16">
            <Link
              to="/case-studies"
              className="group inline-flex items-center px-10 py-4 text-lg font-semibold rounded-2xl bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-purple-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-purple-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-full text-sm font-medium mb-6">
              💬 Client Testimonials
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              What Our <span className="text-purple-600 dark:text-purple-400">Clients Say</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Don't just take our word for it. Here's what our satisfied clients have to say about our exceptional work and commitment to excellence.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="group relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-purple-200 dark:hover:border-purple-600 hover:-translate-y-3 hover:scale-105">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-transparent dark:from-purple-900/20 dark:to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  {/* Quote Icon */}
                  <div className="absolute -top-3 -left-3 w-10 h-10 bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-700 rounded-full flex items-center justify-center text-white text-lg shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                    </svg>
                  </div>
                  
                  {/* Rating Stars - Right Side */}
                  <div className="flex items-center justify-end mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current group-hover:scale-110 transition-transform duration-300" style={{ animationDelay: `${i * 100}ms`, color: '#FFC938' }} />
                    ))}
                  </div>
                  
                  {/* Testimonial Content */}
                  <p className="text-gray-600 dark:text-gray-300 mb-8 italic text-sm leading-relaxed line-clamp-4 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                    "{testimonial.content}"
                  </p>
                  
                  {/* Client Info */}
                  <div className="flex items-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div className="ml-4">
                      <div className="font-bold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">
                        {testimonial.name}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 text-sm group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-300">
                        {testimonial.company}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/20 to-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
          
          {/* Trust Indicators */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center space-x-2 sm:space-x-4 md:space-x-8 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm px-4 sm:px-6 md:px-8 py-3 sm:py-4 rounded-2xl shadow-lg border border-gray-100/50 dark:border-gray-700/50">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">98% Client Satisfaction</span>
              </div>
              <div className="w-px h-4 sm:h-6 bg-gray-300 dark:bg-gray-600"></div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">200+ Happy Clients</span>
              </div>
              <div className="w-px h-4 sm:h-6 bg-gray-300 dark:bg-gray-600"></div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-purple-500 rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">5+ Years Experience</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30 mb-6">
              🚀 Ready to Get Started?
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Transform Your <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Business?</span>
          </h2>
          <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed">
            Let's discuss how we can help you achieve your digital goals and take your business to the next level. Get in touch with our expert team today.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl bg-white text-blue-600 hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              <span>Start Your Project</span>
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/about"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-all duration-300 hover:scale-105"
            >
              <span>Learn More About Us</span>
            </Link>
          </div>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-blue-200 text-sm">Support Available</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Free</div>
              <div className="text-blue-200 text-sm">Consultation</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Fast</div>
              <div className="text-blue-200 text-sm">Response Time</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;